import { Calendar } from "@phosphor-icons/react";
import React, { ChangeEvent, FocusEvent, ReactNode } from "react";
import { v4 as uuid } from "uuid";
import Button from "../button/button.component";

import "./input-field.styles.css";

export interface InputFieldProps<InputType> {
  customClassName?: string;
  type?: "text" | "number" | "password" | "date";
  label?: string;
  placeholder?: string;
  value?: InputType;
  isDisabled?: boolean;
  errorMessage?: string | null;
  icon?: ReactNode;
  iconOnClick?: () => void;
  onChange?: (value: InputType) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
}

const InputField = <
  InputType extends string | number | readonly string[] | Date | undefined
>(
  props: InputFieldProps<InputType>
): JSX.Element => {
  const {
    customClassName,
    label,
    type = "text",
    placeholder = "",
    value,
    isDisabled,
    errorMessage,
    icon,
    iconOnClick,
    onChange,
    onBlur,
  } = props;

  const INPUT_ID = uuid();

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const value =
      event.target.type === "date"
        ? event.target.valueAsDate || undefined
        : event.target.valueAsNumber || event.target.value;
    onChange && onChange(value as InputType);
  };

  return (
    <div
      className={`input-field ${customClassName || ""}${
        isDisabled ? " input-field--disabled" : ""
      }`}
    >
      {label && (
        <label htmlFor={INPUT_ID} className="input-field__label">
          {label}
        </label>
      )}
      <div className="input-field__input-container">
        {!icon && type === "date" && (
          <Button
            label="Date"
            icon={<Calendar weight="light" />}
            customClassName="input-field__icon"
            layout="icon"
            type="button"
            colorStyle="primary"
          />
        )}
        {icon && (
          <Button
            label={label || ""}
            icon={icon}
            customClassName="input-field__icon"
            layout="icon"
            type="button"
            colorStyle="primary"
            onClick={iconOnClick}
          />
        )}

        <input
          id={INPUT_ID}
          type={type}
          className={`input-field__input input-field__input--${type}${
            errorMessage ? " input-field__input--error" : ""
          }`}
          placeholder={placeholder}
          value={
            value instanceof Date ? value.toISOString().split("T")[0] : value
          }
          onChange={handleOnChange}
          disabled={isDisabled}
          onBlur={onBlur}
        />
        <span
          className={`input-field__error-message${
            errorMessage ? " input-field__error-message--visible" : ""
          }`}
        >
          {errorMessage}
        </span>
      </div>
    </div>
  );
};

export default InputField;
